import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDownIcon } from "lucide-react";

import { cn } from "@/lib/utils";

function Accordion({
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

function AccordionItem({
  className,
  firstBorder = false,
  lastBorder = false,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item> & {
  firstBorder?: boolean;
  lastBorder?: boolean;
}) {
  const itemClass = cn(
    "border-b",
    firstBorder ? "first:border-t" : "first:border-t-0",
    lastBorder ? "last:border-b" : "last:border-b-0",
    lastBorder ? "data-[state=open]:pb-4" : "",
    className
  );

  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={itemClass}
      {...props}
    />
  );
}

function AccordionTrigger({
  className,
  children,
  hideChevron = false,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
  hideChevron?: boolean;
}) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          "focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none hover:cursor-pointer focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180",
          className
        )}
        {...props}
      >
        {children}
        {!hideChevron && (
          <ChevronDownIcon className="text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200" />
        )}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({
  className,
  children,
  clipContent = true,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content> & {
  /** When false, content is not clipped (e.g. floating action buttons on cards). */
  clipContent?: boolean;
}) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className={cn(
        "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down text-sm",
        clipContent ? "overflow-hidden" : "overflow-visible"
      )}
      {...props}
    >
      <div className={cn("py-0", className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
